// ForgotPasswordPage.jsx â€” 3-step password reset (email â†’ OTP â†’ new password)

import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  Users, TrendingUp, BarChart3, Shield,
  ArrowLeft, Mail, KeyRound, CheckCircle2,
  Eye, EyeOff, Check, X,
} from "lucide-react";

import ZenLogo from "../assets/images/ZEN LOGO-w 1.png";
import "../css/loginform.css";
import "../css/auth/authPages.css";

const API_URL = "https://apicrm.zenrealestate.in/api";

const passwordRules = (pw = "") => ([
  { label: "At least 8 characters",  ok: pw.length >= 8 },
  { label: "One uppercase letter",   ok: /[A-Z]/.test(pw) },
  { label: "One number",             ok: /[0-9]/.test(pw) },
  { label: "One special character",  ok: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pw) },
]);

const passwordStrength = (pw = "") => {
  const passed = passwordRules(pw).filter(r => r.ok).length;
  if (!pw)         return { level: "",       label: "" };
  if (passed <= 1) return { level: "weak",   label: "Weak" };
  if (passed === 2)return { level: "fair",   label: "Fair" };
  if (passed === 3)return { level: "good",   label: "Good" };
  return                  { level: "strong", label: "Strong" };
};

export default function ForgotPasswordPage() {
  const navigate = useNavigate();

  // step: 1=email, 2=OTP, 3=newPassword, 4=success
  const [step, setStep]     = useState(1);
  const [email, setEmail]   = useState("");
  const [otp, setOtp]       = useState(["", "", "", "", "", ""]);
  const [pw, setPw]         = useState("");
  const [pw2, setPw2]       = useState("");
  const [showPw, setShowPw] = useState(false);
  const [showPw2, setShowPw2] = useState(false);
  const [loading, setLoading] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(600); // 10 minutes
  const [resendCooldown, setResendCooldown] = useState(0);
  const otpRefs = useRef([]);

  // â”€â”€ Countdown timer â”€â”€
  useEffect(() => {
    if (step !== 2 || secondsLeft <= 0) return;
    const t = setInterval(() => setSecondsLeft(s => s - 1), 1000);
    return () => clearInterval(t);
  }, [step, secondsLeft]);

  // â”€â”€ Resend cooldown â”€â”€
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setInterval(() => setResendCooldown(s => s - 1), 1000);
    return () => clearInterval(t);
  }, [resendCooldown]);

  const formatTime = (sec) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
  };

  // â”€â”€ Step 1: Send OTP â”€â”€
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email) return toast.error("Please enter your email");

    setLoading(true);
    try {
      await axios.post(`${API_URL}/auth/forgot-password`, { email: email.trim().toLowerCase() });
      toast.success("If the account exists, an OTP has been sent to your email", {
        position: "top-right", autoClose: 3000, theme: "colored",
      });
      setStep(2);
      setSecondsLeft(600);
      setResendCooldown(60);
      setTimeout(() => otpRefs.current[0]?.focus(), 100);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  // â”€â”€ OTP input handlers â”€â”€
  const handleOtpChange = (idx, val) => {
    if (!/^\d?$/.test(val)) return;
    const next = [...otp];
    next[idx] = val;
    setOtp(next);
    if (val && idx < 5) otpRefs.current[idx + 1]?.focus();
  };

  const handleOtpKeyDown = (idx, e) => {
    if (e.key === "Backspace" && !otp[idx] && idx > 0) {
      otpRefs.current[idx - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pasted.length === 6) {
      e.preventDefault();
      setOtp(pasted.split(""));
      otpRefs.current[5]?.focus();
    }
  };

  // â”€â”€ Resend OTP â”€â”€
  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setLoading(true);
    try {
      await axios.post(`${API_URL}/auth/forgot-password`, { email: email.trim().toLowerCase() });
      toast.success("New OTP sent to your email");
      setOtp(["", "", "", "", "", ""]);
      setSecondsLeft(600);
      setResendCooldown(60);
      otpRefs.current[0]?.focus();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to resend OTP");
    } finally {
      setLoading(false);
    }
  };

  // â”€â”€ Step 2: Verify OTP & proceed to new password â”€â”€
  const handleOtpSubmit = (e) => {
    e.preventDefault();
    const code = otp.join("");
    if (code.length !== 6) return toast.error("Please enter the 6-digit OTP");
    if (secondsLeft <= 0) return toast.error("OTP has expired. Please request a new one.");
    setStep(3);
  };

  // â”€â”€ Step 3: Submit new password â”€â”€
  const handlePwSubmit = async (e) => {
    e.preventDefault();
    const code = otp.join("");
    if (!pw || pw !== pw2) return toast.error("Passwords don't match");
    const rules = passwordRules(pw);
    if (!rules.every(r => r.ok)) return toast.error("Password doesn't meet all requirements");

    setLoading(true);
    try {
      await axios.post(`${API_URL}/auth/reset-password`, {
        email: email.trim().toLowerCase(),
        otp:   code,
        newPassword: pw,
      });
      toast.success("Password reset successfully!");
      setStep(4);
      setTimeout(() => navigate("/"), 2500);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to reset password");
      // If OTP is invalid/expired, go back to step 2
      if (err.response?.data?.message?.toLowerCase().includes("otp")) {
        setStep(2);
      }
    } finally {
      setLoading(false);
    }
  };

  const strength = passwordStrength(pw);
  const rules    = passwordRules(pw);

  return (
    <div className="login-wrapper">
      {/* â”€â”€ Brand panel â”€â”€ */}
      <div className="login-left">
        <div className="login-brand">
          <img src={ZenLogo} alt="Zen Realty" className="login-brand-logo" />
          <h1>Reset Your<br/>Password</h1>
          <p>
            Forgot your password? No worries â€” we'll help you get back into your
            account in just a few quick steps.
          </p>
        </div>

        <div className="login-features">
          <div className="login-feature-item">
            <div className="login-feature-icon"><Mail size={18} /></div>
            <span>Enter your email to receive a one-time code</span>
          </div>
          <div className="login-feature-item">
            <div className="login-feature-icon"><KeyRound size={18} /></div>
            <span>Enter the 6-digit OTP within 10 minutes</span>
          </div>
          <div className="login-feature-item">
            <div className="login-feature-icon"><Shield size={18} /></div>
            <span>Set a new strong password securely</span>
          </div>
        </div>

        <p className="login-left-footer">
          Â© {new Date().getFullYear()} Zen Realty â€” Internal Use Only
        </p>
      </div>

      {/* â”€â”€ Form panel â”€â”€ */}
      <div className="login-right">
        <div className="login-form">
          {/* Step indicator */}
          <div className="step-indicator">
            <div className={`dot ${step >= 1 ? (step > 1 ? "complete" : "active") : ""}`} />
            <div className={`dot ${step >= 2 ? (step > 2 ? "complete" : "active") : ""}`} />
            <div className={`dot ${step >= 3 ? (step > 3 ? "complete" : "active") : ""}`} />
          </div>

          {/* â”€â”€ Step 1: Email â”€â”€ */}
          {step === 1 && (
            <>
              <button className="auth-back-link" onClick={() => navigate("/")}>
                <ArrowLeft size={14} /> Back to login
              </button>
              <div className="login-form-header">
                <h2 className="welcome-text">Forgot Password?</h2>
                <p className="subtitle">Enter your email and we'll send you a 6-digit reset code.</p>
              </div>

              <form onSubmit={handleEmailSubmit}>
                <div className="input-field">
                  <label htmlFor="email">Email Address</label>
                  <input
                    id="email"
                    type="email"
                    placeholder="you@zenrealty.co"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoFocus
                  />
                </div>
                <button type="submit" className="signIn-btn" disabled={loading}>
                  {loading ? "Sendingâ€¦" : "Send Reset Code"}
                </button>
              </form>
            </>
          )}

          {/* â”€â”€ Step 2: OTP â”€â”€ */}
          {step === 2 && (
            <>
              <button className="auth-back-link" onClick={() => setStep(1)}>
                <ArrowLeft size={14} /> Change email
              </button>
              <div className="login-form-header">
                <h2 className="welcome-text">Enter OTP</h2>
                <p className="subtitle">
                  We've sent a 6-digit code to <strong>{email}</strong>
                </p>
              </div>

              <form onSubmit={handleOtpSubmit}>
                <div className="otp-row" onPaste={handleOtpPaste}>
                  {otp.map((digit, i) => (
                    <input
                      key={i}
                      ref={(el) => (otpRefs.current[i] = el)}
                      className={`otp-box ${digit ? "filled" : ""}`}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleOtpChange(i, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(i, e)}
                    />
                  ))}
                </div>

                <p className={`otp-timer ${secondsLeft <= 0 ? "expired" : ""}`}>
                  {secondsLeft > 0
                    ? <>OTP expires in <strong>{formatTime(secondsLeft)}</strong></>
                    : <>OTP has <strong>expired</strong>. Please request a new one.</>
                  }
                </p>

                <div style={{ textAlign: "center", marginBottom: 16 }}>
                  <button
                    type="button"
                    className="resend-btn"
                    onClick={handleResend}
                    disabled={resendCooldown > 0 || loading}
                  >
                    {resendCooldown > 0
                      ? `Resend in ${resendCooldown}s`
                      : "Didn't receive? Resend code"}
                  </button>
                </div>

                <button type="submit" className="signIn-btn" disabled={loading || otp.join("").length !== 6}>
                  {loading ? "Verifyingâ€¦" : "Verify & Continue"}
                </button>
              </form>
            </>
          )}

          {/* â”€â”€ Step 3: New Password â”€â”€ */}
          {step === 3 && (
            <>
              <button className="auth-back-link" onClick={() => setStep(2)}>
                <ArrowLeft size={14} /> Back
              </button>
              <div className="login-form-header">
                <h2 className="welcome-text">Set New Password</h2>
                <p className="subtitle">Choose a strong password to secure your account.</p>
              </div>

              <form onSubmit={handlePwSubmit}>
                <div className="input-field">
                  <label htmlFor="newPw">New Password</label>
                  <div className="input-with-icon">
                    <input
                      id="newPw"
                      type={showPw ? "text" : "password"}
                      placeholder="Enter new password"
                      value={pw}
                      onChange={(e) => setPw(e.target.value)}
                      required
                      autoFocus
                    />
                    <button type="button" className="input-icon-btn" onClick={() => setShowPw(v => !v)} tabIndex={-1}>
                      {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {pw && (
                    <>
                      <div className="password-strength">
                        <div className={`strength-bar ${strength.level}`}>
                          <div /><div /><div /><div />
                        </div>
                        {strength.label && <span className={`strength-label ${strength.level}`}>{strength.label}</span>}
                      </div>
                      <div className="password-rules">
                        {rules.map((r, i) => (
                          <div key={i} className={`password-rule ${r.ok ? "satisfied" : ""}`}>
                            {r.ok ? <Check size={12} /> : <X size={12} />}
                            <span>{r.label}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>

                <div className="input-field">
                  <label htmlFor="newPw2">Confirm Password</label>
                  <div className="input-with-icon">
                    <input
                      id="newPw2"
                      type={showPw2 ? "text" : "password"}
                      placeholder="Re-enter new password"
                      value={pw2}
                      onChange={(e) => setPw2(e.target.value)}
                      required
                    />
                    <button type="button" className="input-icon-btn" onClick={() => setShowPw2(v => !v)} tabIndex={-1}>
                      {showPw2 ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {pw2 && pw !== pw2 && (
                    <span className="error-text">Passwords don't match</span>
                  )}
                </div>

                <button type="submit" className="signIn-btn" disabled={loading || !rules.every(r => r.ok) || pw !== pw2}>
                  {loading ? "Resettingâ€¦" : "Reset Password"}
                </button>
              </form>
            </>
          )}

          {/* â”€â”€ Step 4: Success â”€â”€ */}
          {step === 4 && (
            <>
              <div className="verify-status success">
                <div className="verify-status-icon">
                  <CheckCircle2 size={32} />
                </div>
                <h3>Password Reset Successfully!</h3>
                <p>Redirecting you to loginâ€¦</p>
              </div>
              <Link to="/" className="signIn-btn" style={{ display: "block", textAlign: "center", textDecoration: "none" }}>
                Go to Login
              </Link>
            </>
          )}
        </div>
      </div>

      <ToastContainer />
    </div>
  );
}
